import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Plus, Edit, Shapes, X, Loader2 } from 'lucide-react'
import toast from 'react-hot-toast'
import { api } from '@/lib/api'

interface Molde {
  id: number
  codigo: string
  descricao: string
  tipo: string
  qtde_gavetas: number
  tiras_por_bloco: number
  pecas_por_ciclo: number
  preco_custo: string
  fornecedor_id: number | null
  vida_util_ciclos: number
  ciclos_usados: number
  ultima_manutencao: string | null
  situacao: string
  observacoes: string | null
}

const TIPOS = ['MOLDE', 'ESTAMPO', 'CUNHO', 'MATRIZ', 'OUTROS']
const SIT_COR: Record<string, string> = {
  ATIVO: 'bg-emerald-100 text-emerald-800',
  MANUTENCAO: 'bg-amber-100 text-amber-800',
  INATIVO: 'bg-ink-200 text-ink-600',
}

export default function IndMoldesPage() {
  const qc = useQueryClient()
  const [modal, setModal] = useState<Molde | null | 'new'>(null)

  const { data = [], isLoading } = useQuery<Molde[]>({
    queryKey: ['ind-moldes'],
    queryFn: async () => (await api.get('/industrial/moldes')).data,
  })

  return (
    <div className="p-6 space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Shapes className="text-brand-600" /> Moldes e Estampos
          </h1>
          <p className="text-sm text-ink-500">Cadastro de moldes, cunhos, estampos e matrizes</p>
        </div>
        <button className="btn-primary" onClick={() => setModal('new')}>
          <Plus size={14} /> Novo molde
        </button>
      </div>

      <div className="bg-white border border-ink-200 rounded-lg overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-ink-50">
            <tr>
              <th className="text-left text-xs font-semibold text-ink-600 uppercase px-3 py-2">Código</th>
              <th className="text-left text-xs font-semibold text-ink-600 uppercase px-3 py-2">Descrição</th>
              <th className="text-left text-xs font-semibold text-ink-600 uppercase px-3 py-2">Tipo</th>
              <th className="text-center text-xs font-semibold text-ink-600 uppercase px-3 py-2">Gavetas × Tiras</th>
              <th className="text-right text-xs font-semibold text-ink-600 uppercase px-3 py-2">Peças/ciclo</th>
              <th className="text-right text-xs font-semibold text-ink-600 uppercase px-3 py-2">Custo R$</th>
              <th className="text-center text-xs font-semibold text-ink-600 uppercase px-3 py-2">Vida útil</th>
              <th className="text-center text-xs font-semibold text-ink-600 uppercase px-3 py-2">Situação</th>
              <th className="w-12"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-ink-100">
            {isLoading && <tr><td colSpan={9} className="text-center py-6 text-ink-400">Carregando…</td></tr>}
            {!isLoading && data.length === 0 && (
              <tr><td colSpan={9} className="text-center py-10 text-ink-500">
                Nenhum molde cadastrado ainda.
              </td></tr>
            )}
            {data.map((m) => {
              const pctUso = m.vida_util_ciclos > 0 ? (m.ciclos_usados / m.vida_util_ciclos) * 100 : 0
              return (
                <tr key={m.id}
                  onClick={() => setModal(m)}
                  className="hover:bg-violet-50 cursor-pointer">
                  <td className="px-3 py-2.5 font-mono text-xs">{m.codigo}</td>
                  <td className="px-3 py-2.5 font-medium">{m.descricao}</td>
                  <td className="px-3 py-2.5 text-xs">{m.tipo}</td>
                  <td className="px-3 py-2.5 text-center text-xs font-mono">
                    {m.qtde_gavetas} × {m.tiras_por_bloco}
                  </td>
                  <td className="px-3 py-2.5 text-right font-semibold">{m.pecas_por_ciclo}</td>
                  <td className="px-3 py-2.5 text-right text-xs">{Number(m.preco_custo).toFixed(2)}</td>
                  <td className="px-3 py-2.5 text-center text-xs">
                    {m.vida_util_ciclos > 0 ? (
                      <div>
                        <div>{m.ciclos_usados}/{m.vida_util_ciclos}</div>
                        <div className="w-16 h-1 bg-ink-100 rounded mt-0.5 mx-auto overflow-hidden">
                          <div
                            className={`h-full ${pctUso > 80 ? 'bg-red-500' : pctUso > 50 ? 'bg-amber-500' : 'bg-emerald-500'}`}
                            style={{ width: `${Math.min(pctUso, 100)}%` }}
                          />
                        </div>
                      </div>
                    ) : (
                      <span className="text-ink-400">—</span>
                    )}
                  </td>
                  <td className="px-3 py-2.5 text-center">
                    <span className={`text-[11px] font-medium px-2 py-0.5 rounded ${SIT_COR[m.situacao]}`}>
                      {m.situacao}
                    </span>
                  </td>
                  <td className="px-3 py-2.5" onClick={e => e.stopPropagation()}>
                    <button onClick={() => setModal(m)} className="text-ink-500 hover:text-brand-600 p-1">
                      <Edit size={14} />
                    </button>
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>

      {modal && (
        <MoldeModal
          molde={modal === 'new' ? null : modal}
          onClose={() => setModal(null)}
          onSuccess={() => { qc.invalidateQueries({ queryKey: ['ind-moldes'] }); setModal(null) }}
        />
      )}
    </div>
  )
}


function MoldeModal({ molde, onClose, onSuccess }: { molde: Molde | null; onClose: () => void; onSuccess: () => void }) {
  const [form, setForm] = useState({
    codigo: molde?.codigo || '',
    descricao: molde?.descricao || '',
    tipo: molde?.tipo || 'MOLDE',
    qtde_gavetas: molde?.qtde_gavetas || 1,
    tiras_por_bloco: molde?.tiras_por_bloco || 1,
    pecas_por_ciclo: molde?.pecas_por_ciclo || 0,
    preco_custo: Number(molde?.preco_custo) || 0,
    vida_util_ciclos: molde?.vida_util_ciclos || 0,
    observacoes: molde?.observacoes || '',
  })

  // Auto-calcula pecas_por_ciclo quando gavetas ou tiras mudam
  const pecasCalculadas = form.qtde_gavetas * form.tiras_por_bloco

  const mut = useMutation({
    mutationFn: async () => {
      const payload = { ...form, pecas_por_ciclo: form.pecas_por_ciclo || pecasCalculadas }
      if (molde) return (await api.put(`/industrial/moldes/${molde.id}`, payload)).data
      return (await api.post('/industrial/moldes', payload)).data
    },
    onSuccess: () => {
      toast.success(molde ? 'Molde atualizado' : 'Molde cadastrado')
      onSuccess()
    },
    onError: (e: any) => toast.error(e?.response?.data?.detail || 'Erro'),
  })

  const valido = form.codigo.trim() && form.descricao.trim()

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="border-b border-ink-200 p-4 flex items-center justify-between sticky top-0 bg-white">
          <h2 className="text-lg font-semibold flex items-center gap-2">
            <Shapes size={18} /> {molde ? `Editar ${molde.codigo}` : 'Novo molde'}
          </h2>
          <button onClick={onClose}><X size={18} /></button>
        </div>
        <div className="p-4 space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="label">Código *</label>
              <input className="input" value={form.codigo}
                onChange={(e) => setForm({ ...form, codigo: e.target.value.toUpperCase() })}
                placeholder="Ex: MLD-001" />
            </div>
            <div>
              <label className="label">Tipo</label>
              <select className="input" value={form.tipo}
                onChange={(e) => setForm({ ...form, tipo: e.target.value })}>
                {TIPOS.map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
          </div>
          <div>
            <label className="label">Descrição *</label>
            <input className="input" value={form.descricao}
              onChange={(e) => setForm({ ...form, descricao: e.target.value })}
              placeholder="Ex: Molde vedante redondo 50mm" />
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className="label">Qtde gavetas</label>
              <input type="number" min="1" className="input" value={form.qtde_gavetas}
                onChange={(e) => setForm({ ...form, qtde_gavetas: Number(e.target.value) || 1 })} />
            </div>
            <div>
              <label className="label">Tiras por bloco</label>
              <input type="number" min="1" className="input" value={form.tiras_por_bloco}
                onChange={(e) => setForm({ ...form, tiras_por_bloco: Number(e.target.value) || 1 })} />
            </div>
            <div>
              <label className="label">Peças/ciclo (calc = {pecasCalculadas})</label>
              <input type="number" min="1" className="input" value={form.pecas_por_ciclo || ''}
                placeholder={`auto: ${pecasCalculadas}`}
                onChange={(e) => setForm({ ...form, pecas_por_ciclo: Number(e.target.value) || 0 })} />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="label">Preço de custo (R$)</label>
              <input type="number" step="0.01" className="input" value={form.preco_custo}
                onChange={(e) => setForm({ ...form, preco_custo: Number(e.target.value) })} />
            </div>
            <div>
              <label className="label">Vida útil (ciclos, 0 = ilimitada)</label>
              <input type="number" min="0" className="input" value={form.vida_util_ciclos}
                onChange={(e) => setForm({ ...form, vida_util_ciclos: Number(e.target.value) })} />
            </div>
          </div>
          <div>
            <label className="label">Observações</label>
            <textarea className="input" rows={2} value={form.observacoes}
              onChange={(e) => setForm({ ...form, observacoes: e.target.value })} />
          </div>
          <div className="flex gap-2 pt-2">
            <button className="btn-ghost flex-1" onClick={onClose}>Cancelar</button>
            <button className="btn-primary flex-1" disabled={!valido || mut.isPending}
              onClick={() => mut.mutate()}>
              {mut.isPending ? <Loader2 className="animate-spin" size={14} /> : 'Salvar'}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
